import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import * as companyService from '../services/companyService';
import * as activityService from '../services/activityService';
import * as systemHealthService from '../services/systemHealthService';
import { getSubscriptionKPIs, getFinanceDashboard } from '../services/subscriptionService';
import type { PlatformStats } from '../services/companyService';
import type { Activity } from '../services/activityService';
import type { SystemMetrics } from '../services/systemHealthService';
import type { SubscriptionKPIs, FinanceDashboardData } from '../services/subscriptionService';

function PlatformAdminDashboardV2(): React.ReactElement {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
  const [subscriptionKPIs, setSubscriptionKPIs] = useState<SubscriptionKPIs | null>(null);
  const [financeData, setFinanceData] = useState<FinanceDashboardData | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemMetrics | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isActivityExpanded, setIsActivityExpanded] = useState(() => {
    const saved = localStorage.getItem('platform_dashboard_v2_activity_expanded');
    return saved !== null ? saved === 'true' : true;
  });

  useEffect(() => {
    async function loadAll() {
      if (!token) return;
      setLoading(true);
      setError(null);

      try {
        const [statsResult, subKpiResult, financeResult, healthResult, activityResult] = await Promise.allSettled([
          companyService.getPlatformStats(token),
          getSubscriptionKPIs(token),
          getFinanceDashboard(token),
          systemHealthService.getSystemHealth('24h', token),
          activityService.getSystemActivities({ limit: 25 }, token),
        ]);

        if (statsResult.status === 'fulfilled') setPlatformStats(statsResult.value);
        if (subKpiResult.status === 'fulfilled') setSubscriptionKPIs(subKpiResult.value);
        if (financeResult.status === 'fulfilled') setFinanceData(financeResult.value);
        if (healthResult.status === 'fulfilled') setSystemHealth(healthResult.value.metrics);
        if (activityResult.status === 'fulfilled') setActivities(activityResult.value);

        const allFailed = [statsResult, subKpiResult, financeResult, healthResult, activityResult].every(
          (r) => r.status === 'rejected'
        );
        if (allFailed) setError('Failed to load dashboard data. Please try again.');
      } catch {
        setError('An unexpected error occurred.');
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  }, [token]);

  const toggleActivityExpanded = () => {
    const next = !isActivityExpanded;
    setIsActivityExpanded(next);
    localStorage.setItem('platform_dashboard_v2_activity_expanded', String(next));
  };

  const getRelativeTime = (dateStr: string): string => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatCurrency = (value: number) =>
    `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    if (days > 0) return `${days}d ${hours}h`;
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  // Subscription status bar chart data
  const subscriptionBarData = useMemo(() => {
    if (!subscriptionKPIs) return [];
    return [
      { status: 'Active', count: subscriptionKPIs.active_subscriptions, color: '#10b981' },
      { status: 'Trial', count: subscriptionKPIs.trial_companies, color: '#f59e0b' },
      { status: 'Suspended', count: subscriptionKPIs.suspended_companies, color: '#ef4444' },
      { status: 'Cancelled', count: subscriptionKPIs.cancelled_companies, color: '#6b7280' },
    ].filter(d => d.count > 0);
  }, [subscriptionKPIs]);

  // MRR trend line chart data
  const mrrChartData = useMemo(() => {
    if (!financeData?.mrr_trend || financeData.mrr_trend.length === 0) return [];
    return [
      {
        id: 'MRR',
        data: financeData.mrr_trend.map((p) => {
          const [year, month] = p.month.split('-');
          const date = new Date(parseInt(year), parseInt(month) - 1);
          return { x: date.toLocaleString('en-US', { month: 'short', year: '2-digit' }), y: p.mrr };
        }),
      },
    ];
  }, [financeData]);

  // Registration trend line chart
  const registrationChartData = useMemo(() => {
    if (!financeData?.monthly_registrations || financeData.monthly_registrations.length === 0) return [];
    return [
      {
        id: 'New Companies',
        data: financeData.monthly_registrations.map((p) => {
          const [year, month] = p.month.split('-');
          const date = new Date(parseInt(year), parseInt(month) - 1);
          return { x: date.toLocaleString('en-US', { month: 'short', year: '2-digit' }), y: p.companies };
        }),
      },
    ];
  }, [financeData]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'trial': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      case 'suspended': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'cancelled': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const mrrGrowth = useMemo(() => {
    if (!financeData?.mrr_trend || financeData.mrr_trend.length < 2) return null;
    const first = financeData.mrr_trend[0].mrr;
    const last = financeData.mrr_trend[financeData.mrr_trend.length - 1].mrr;
    if (first === 0) return null;
    return ((last - first) / first) * 100;
  }, [financeData]);

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold text-white">Platform Command Center</h1>
          <p className="text-indigo-100 mt-1">Cross-company metrics, revenue, and system health at a glance</p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* KPI Row 1: Platform Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Companies */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Companies</p>
              <span className="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
                <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{platformStats?.total_companies ?? '-'}</p>
            {subscriptionKPIs && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                <span className="text-green-600 dark:text-green-400 font-medium">{subscriptionKPIs.active_subscriptions} active</span>
                {subscriptionKPIs.trial_companies > 0 && (
                  <span> · <span className="text-amber-600 dark:text-amber-400 font-medium">{subscriptionKPIs.trial_companies} trial</span></span>
                )}
              </p>
            )}
          </div>

          {/* Users */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Users</p>
              <span className="p-1.5 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{platformStats?.total_users ?? '-'}</p>
            {financeData && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {financeData.total_billable_users} billable
              </p>
            )}
          </div>

          {/* MRR */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">MRR</p>
              <span className="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
                <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{financeData ? formatCurrency(financeData.mrr) : '-'}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {financeData ? (
                <>ARR {formatCurrency(financeData.arr)}</>
              ) : '—'}
              {mrrGrowth !== null && (
                <span className={`ml-2 font-medium ${mrrGrowth >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                  {mrrGrowth >= 0 ? '+' : ''}{mrrGrowth.toFixed(1)}%
                </span>
              )}
            </p>
          </div>

          {/* System Health */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">System Health</p>
              <span className={`p-1.5 rounded-lg ${
                systemHealth && systemHealth.errorRate < 1
                  ? 'bg-green-50 dark:bg-green-900/30'
                  : systemHealth && systemHealth.errorRate < 5
                  ? 'bg-amber-50 dark:bg-amber-900/30'
                  : 'bg-red-50 dark:bg-red-900/30'
              }`}>
                <svg className={`w-4 h-4 ${
                  systemHealth && systemHealth.errorRate < 1
                    ? 'text-green-600 dark:text-green-400'
                    : systemHealth && systemHealth.errorRate < 5
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-red-600 dark:text-red-400'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
            </div>
            {systemHealth ? (
              <>
                <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {systemHealth.errorRate < 1 ? 'Healthy' : systemHealth.errorRate < 5 ? 'Warning' : 'Critical'}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Uptime {formatUptime(systemHealth.uptime)} · {systemHealth.errorRate.toFixed(1)}% error rate
                </p>
              </>
            ) : (
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">-</p>
            )}
          </div>
        </div>

        {/* KPI Row 2: Revenue & Subscription Detail */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Avg Revenue per Company */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Avg Revenue / Company</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {financeData ? formatCurrency(financeData.avg_revenue_per_company) : '-'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {financeData ? `${financeData.paying_companies} paying` : '—'}
            </p>
          </div>

          {/* Projects */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Total Projects</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{platformStats?.total_projects ?? '-'}</p>
          </div>

          {/* Requirements */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Total Requirements</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{platformStats?.total_requirements ?? '-'}</p>
          </div>

          {/* Expiring Trials */}
          <div className={`rounded-lg shadow p-5 ${
            subscriptionKPIs && subscriptionKPIs.expiring_trials_7days > 0
              ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
              : 'bg-white dark:bg-gray-800'
          }`}>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Expiring Trials (7d)</p>
            <p className={`text-2xl font-bold ${
              subscriptionKPIs && subscriptionKPIs.expiring_trials_7days > 0
                ? 'text-amber-700 dark:text-amber-400'
                : 'text-gray-900 dark:text-gray-100'
            }`}>
              {subscriptionKPIs?.expiring_trials_7days ?? '-'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">need attention</p>
          </div>
        </div>

        {/* Main Content: 2/3 + 1/3 layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left 2/3 */}
          <div className="lg:col-span-2 space-y-6">

            {/* MRR Trend Chart */}
            {mrrChartData.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">MRR Trend</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Monthly recurring revenue over time</p>
                </div>
                <div className="px-6 py-4" style={{ height: 280 }}>
                  <ResponsiveLine
                    data={mrrChartData}
                    margin={{ top: 20, right: 30, bottom: 50, left: 60 }}
                    xScale={{ type: 'point' }}
                    yScale={{ type: 'linear', min: 0, max: 'auto' }}
                    axisBottom={{
                      tickRotation: -45,
                      legendOffset: 40,
                      legendPosition: 'middle' as const,
                    }}
                    axisLeft={{
                      legend: 'MRR ($)',
                      legendOffset: -50,
                      legendPosition: 'middle' as const,
                      format: (v: number) => `$${v}`,
                    }}
                    colors={['#4f46e5']}
                    lineWidth={2}
                    pointSize={6}
                    pointColor={{ from: 'color' }}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    enableArea={true}
                    areaOpacity={0.1}
                    useMesh={true}
                    enableSlices="x"
                    sliceTooltip={({ slice }: any) => (
                      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{slice.points[0].data.x}</p>
                        {slice.points.map((point: any) => (
                          <div key={point.id} className="flex items-center gap-2 text-sm">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: point.serieColor }} />
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(point.data.y)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    theme={{
                      axis: {
                        ticks: { text: { fill: '#9ca3af', fontSize: 11 } },
                        legend: { text: { fill: '#9ca3af', fontSize: 12 } },
                      },
                      grid: { line: { stroke: '#e5e7eb', strokeWidth: 1 } },
                    }}
                  />
                </div>
              </div>
            )}

            {/* Subscription Status + Company Registrations side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Subscription Status Bar Chart */}
              {subscriptionBarData.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Subscription Status</h3>
                  </div>
                  <div className="px-6 py-4" style={{ height: 220 }}>
                    <ResponsiveBar
                      data={subscriptionBarData}
                      keys={['count']}
                      indexBy="status"
                      margin={{ top: 10, right: 20, bottom: 40, left: 40 }}
                      padding={0.4}
                      colors={({ data }: any) => data.color}
                      borderRadius={4}
                      axisBottom={{
                        tickSize: 0,
                        tickPadding: 8,
                      }}
                      axisLeft={{
                        tickSize: 0,
                        tickPadding: 8,
                      }}
                      enableLabel={true}
                      labelTextColor="#ffffff"
                      theme={{
                        axis: {
                          ticks: { text: { fill: '#9ca3af', fontSize: 11 } },
                        },
                        grid: { line: { stroke: '#e5e7eb', strokeWidth: 1 } },
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Company Registration Trend */}
              {registrationChartData.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">New Registrations</h3>
                  </div>
                  <div className="px-6 py-4" style={{ height: 220 }}>
                    <ResponsiveLine
                      data={registrationChartData}
                      margin={{ top: 10, right: 20, bottom: 40, left: 40 }}
                      xScale={{ type: 'point' }}
                      yScale={{ type: 'linear', min: 0, max: 'auto' }}
                      axisBottom={{ tickRotation: -45 }}
                      axisLeft={{ tickSize: 0, tickPadding: 8 }}
                      colors={['#8b5cf6']}
                      lineWidth={2}
                      pointSize={5}
                      pointColor={{ from: 'color' }}
                      enableArea={true}
                      areaOpacity={0.08}
                      useMesh={true}
                      theme={{
                        axis: {
                          ticks: { text: { fill: '#9ca3af', fontSize: 11 } },
                        },
                        grid: { line: { stroke: '#e5e7eb', strokeWidth: 1 } },
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Top Companies by Revenue */}
            {financeData && financeData.top_companies.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Top Companies by Revenue</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Highest contributing accounts</p>
                  </div>
                  <button
                    onClick={() => navigate('/admin/company-subscriptions')}
                    className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                  >
                    View All
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Company</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Users</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">MRR</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Discount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {financeData.top_companies.map((company, i) => (
                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="px-6 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{company.name}</td>
                          <td className="px-6 py-3 text-sm text-gray-600 dark:text-gray-300">{company.users}</td>
                          <td className="px-6 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{formatCurrency(company.mrr)}</td>
                          <td className="px-6 py-3">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(company.status)}`}>
                              {company.status}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-600 dark:text-gray-300">
                            {company.discount > 0 ? `${company.discount}%` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Revenue by Role */}
            {financeData && financeData.revenue_by_role.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Revenue by Role</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Per-seat revenue breakdown</p>
                  </div>
                  <button
                    onClick={() => navigate('/admin/pricing-plans')}
                    className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                  >
                    Manage Plans
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Users</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Unit Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {financeData.revenue_by_role.map((role, i) => (
                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="px-6 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                            {role.role.replace(/_/g, ' ')}
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-600 dark:text-gray-300">{role.users}</td>
                          <td className="px-6 py-3 text-sm text-gray-600 dark:text-gray-300">{formatCurrency(role.unit_price)}</td>
                          <td className="px-6 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{formatCurrency(role.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Tier Breakdown */}
            {subscriptionKPIs && subscriptionKPIs.tier_breakdown.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Companies by Tier</h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {subscriptionKPIs.tier_breakdown.map((tier, i) => (
                      <div
                        key={i}
                        className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-center"
                      >
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{tier.count}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 capitalize mt-1">{tier.tier || 'Unassigned'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right 1/3 */}
          <div className="space-y-6">

            {/* Quick Navigation */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Quick Actions</h3>
              </div>
              <div className="p-4 flex flex-wrap gap-2">
                {[
                  { label: 'Companies', path: '/admin/companies' },
                  { label: 'Users', path: '/users' },
                  { label: 'System Health', path: '/admin/system-health' },
                  { label: 'AI Config', path: '/admin/ai-config' },
                  { label: 'Finances', path: '/admin/finances' },
                  { label: 'User Map', path: '/admin/user-location-map' },
                ].map((action) => (
                  <button
                    key={action.path}
                    onClick={() => navigate(action.path)}
                    className="px-3.5 py-1.5 rounded-full text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 transition-colors shadow-sm"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>

            {/* System Health Details */}
            {systemHealth && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">System Stats (24h)</h3>
                  <button
                    onClick={() => navigate('/admin/system-health')}
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                  >
                    Details
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">API Calls</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{systemHealth.totalApiCalls.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Logins</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{systemHealth.totalLogins.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Errors</span>
                    <span className={`text-sm font-medium ${systemHealth.totalErrors > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {systemHealth.totalErrors}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Error Rate</span>
                    <span className={`text-sm font-medium ${systemHealth.errorRate > 1 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                      {systemHealth.errorRate.toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Avg Response</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{systemHealth.avgResponseTime.toFixed(0)}ms</span>
                  </div>
                </div>
              </div>
            )}

            {/* Activity Feed */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <button
                onClick={toggleActivityExpanded}
                className="w-full px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-t-lg"
              >
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">System Activity</h3>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isActivityExpanded ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              {isActivityExpanded && (
                <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-96 overflow-y-auto">
                  {activities.length === 0 ? (
                    <p className="px-6 py-8 text-sm text-gray-400 dark:text-gray-500 text-center">No recent activity</p>
                  ) : (
                    activities.slice(0, 20).map((activity) => (
                      <div key={activity.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                              <span className="font-medium">
                                {activity.user_first_name} {activity.user_last_name}
                              </span>
                              {' '}{activity.description}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {activity.company_name && (
                                <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium truncate max-w-[120px]">
                                  {activity.company_name}
                                </span>
                              )}
                              {activity.project_code && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                  {activity.project_code}
                                </span>
                              )}
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                {getRelativeTime(activity.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default PlatformAdminDashboardV2;
